import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Download, LogOut, RefreshCw, ChevronLeft, ChevronRight,
  Users, Sparkles, MessageSquare, Handshake, Filter, X,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const CONTACT_TYPE_LABELS = { demo: 'Demo Request', inquiry: 'General Inquiry', partner: 'Partnership' };
const CONTACT_TYPE_COLORS = {
  demo:    { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  inquiry: { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  partner: { bg: '#faf5ff', color: '#7c3aed', border: '#e9d5ff' },
};

const INDUSTRIES = ['hvac', 'mep', 'manufacturing', 'tender', 'sme'];
const INDUSTRY_LABELS = { hvac: 'HVAC', mep: 'MEP Engineering', manufacturing: 'Industrial Suppliers', tender: 'Tender Teams', sme: 'SME Business' };

const COUNTRIES = [
  { value: 'ae', label: 'UAE 🇦🇪' }, { value: 'sa', label: 'Saudi Arabia 🇸🇦' },
  { value: 'kw', label: 'Kuwait 🇰🇼' }, { value: 'qa', label: 'Qatar 🇶🇦' },
  { value: 'bh', label: 'Bahrain 🇧🇭' }, { value: 'om', label: 'Oman 🇴🇲' },
  { value: 'eg', label: 'Egypt 🇪🇬' }, { value: 'ma', label: 'Morocco 🇲🇦' },
  { value: 'sd', label: 'Sudan 🇸🇩' }, { value: 'et', label: 'Ethiopia 🇪🇹' },
  { value: 'ke', label: 'Kenya 🇰🇪' }, { value: 'tz', label: 'Tanzania 🇹🇿' },
  { value: 'ug', label: 'Uganda 🇺🇬' }, { value: 'rw', label: 'Rwanda 🇷🇼' },
  { value: 'other', label: 'Other 🌍' },
];
const COUNTRY_MAP = Object.fromEntries(COUNTRIES.map(c => [c.value, c.label]));

function fmt(date) {
  return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function AdminLeads() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('adminToken');

  const [leads,   setLeads]   = useState([]);
  const [stats,   setStats]   = useState({ total: 0, demo: 0, inquiry: 0, partner: 0 });
  const [loading, setLoading] = useState(true);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [pages,   setPages]   = useState(1);

  const [search,      setSearch]      = useState('');
  const [contactType, setContactType] = useState('');
  const [country,     setCountry]     = useState('');
  const [industry,    setIndustry]    = useState('');
  const [from,        setFrom]        = useState('');
  const [to,          setTo]          = useState('');

  const [expanded,    setExpanded]    = useState(null); // row id for message expand
  const [exporting,   setExporting]   = useState(false);

  // Redirect if no token
  useEffect(() => { if (!token) navigate('/admin/login'); }, [token, navigate]);

  const buildParams = useCallback((extraPage) => {
    const p = new URLSearchParams();
    if (search)      p.set('search',      search);
    if (contactType) p.set('contactType', contactType);
    if (country)     p.set('country',     country);
    if (industry)    p.set('industry',    industry);
    if (from)        p.set('from',        from);
    if (to)          p.set('to',          to);
    p.set('page',  extraPage || page);
    p.set('limit', 20);
    return p.toString();
  }, [search, contactType, country, industry, from, to, page]);

  const fetchLeads = useCallback(async (targetPage = page) => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (search)      p.set('search',      search);
      if (contactType) p.set('contactType', contactType);
      if (country)     p.set('country',     country);
      if (industry)    p.set('industry',    industry);
      if (from)        p.set('from',        from);
      if (to)          p.set('to',          to);
      p.set('page', targetPage);
      p.set('limit', 20);

      const res  = await fetch(`${API_BASE}/api/leads?${p.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 403) { sessionStorage.removeItem('adminToken'); navigate('/admin/login'); return; }
      const data = await res.json();
      setLeads(data.data || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setStats(data.stats || { total: 0, demo: 0, inquiry: 0, partner: 0 });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token, navigate, search, contactType, country, industry, from, to]);

  // Fetch on filter/page change (debounce search)
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchLeads(1); }, search ? 400 : 0);
    return () => clearTimeout(t);
  }, [search, contactType, country, industry, from, to]); // eslint-disable-line

  useEffect(() => { fetchLeads(page); }, [page]); // eslint-disable-line

  const handleExport = async () => {
    setExporting(true);
    try {
      const p = new URLSearchParams();
      if (search)      p.set('search',      search);
      if (contactType) p.set('contactType', contactType);
      if (country)     p.set('country',     country);
      if (industry)    p.set('industry',    industry);
      if (from)        p.set('from',        from);
      if (to)          p.set('to',          to);

      const res  = await fetch(`${API_BASE}/api/leads/export?${p.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `tuly-leads-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    finally { setExporting(false); }
  };

  const clearFilters = () => { setSearch(''); setContactType(''); setCountry(''); setIndustry(''); setFrom(''); setTo(''); };
  const hasFilters   = search || contactType || country || industry || from || to;

  const S = styles;

  return (
    <div style={S.page}>

      {/* ── NAVBAR ── */}
      <nav style={S.nav}>
        <div style={S.navInner}>
          <div style={S.navLeft}>
            <img src="/tuly.png" alt="Tuly" style={{ height: '24px', width: 'auto' }} />
            <div style={S.navDivider} />
            <span style={S.navLabel}>Admin</span>
            <span style={S.navChip}>Leads Dashboard</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button onClick={() => navigate('/admin/chats')} style={{ ...S.iconBtn, display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '12px', color: '#64748b', fontWeight: 500 }} title="Chat Sessions">
              <MessageSquare size={14} color="#64748b" /> Chat Sessions
            </button>
            <button onClick={() => navigate('/admin/clients')} style={{ ...S.iconBtn, display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '12px', color: '#64748b', fontWeight: 500 }} title="Clients">
              <Users size={14} color="#64748b" /> Clients
            </button>
            <button onClick={() => fetchLeads(page)} style={S.iconBtn} title="Refresh">
              <RefreshCw size={15} color="#94a3b8" />
            </button>
            <button
              onClick={() => { sessionStorage.removeItem('adminToken'); navigate('/admin/login'); }}
              style={S.logoutBtn}
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </nav>

      <div style={S.content}>

        {/* ── STATS ROW ── */}
        <div style={S.statsRow}>
          {[
            { label: 'Total Leads',   value: stats.total,   icon: Users,         color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
            { label: 'Demo Requests', value: stats.demo,    icon: Sparkles,      color: '#7c3aed', bg: '#faf5ff', border: '#e9d5ff' },
            { label: 'Inquiries',     value: stats.inquiry, icon: MessageSquare, color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
            { label: 'Partnerships',  value: stats.partner, icon: Handshake,     color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
          ].map((s) => (
            <div key={s.label} style={{ ...S.statCard, borderColor: s.border }}>
              <div style={{ ...S.statIcon, background: s.bg, borderColor: s.border }}>
                <s.icon size={16} color={s.color} />
              </div>
              <div>
                <p style={S.statValue}>{s.value}</p>
                <p style={S.statLabel}>{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── FILTER BAR ── */}
        <div style={S.filterBar}>
          {/* Search */}
          <div style={S.searchWrap}>
            <Search size={15} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, company…"
              style={S.searchInput}
            />
            {search && <button onClick={() => setSearch('')} style={S.clearX}><X size={13} /></button>}
          </div>

          {/* Dropdowns */}
          <select value={contactType} onChange={(e) => setContactType(e.target.value)} style={S.select}>
            <option value="">All Types</option>
            <option value="demo">Demo Request</option>
            <option value="inquiry">General Inquiry</option>
            <option value="partner">Partnership</option>
          </select>

          <select value={country} onChange={(e) => setCountry(e.target.value)} style={S.select}>
            <option value="">All Countries</option>
            {COUNTRIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>

          <select value={industry} onChange={(e) => setIndustry(e.target.value)} style={S.select}>
            <option value="">All Industries</option>
            {INDUSTRIES.map(k => <option key={k} value={k}>{INDUSTRY_LABELS[k]}</option>)}
          </select>

          {/* Date range */}
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ ...S.select, color: from ? '#0f172a' : '#94a3b8' }} title="From date" />
          <input type="date" value={to}   onChange={(e) => setTo(e.target.value)}   style={{ ...S.select, color: to   ? '#0f172a' : '#94a3b8' }} title="To date" />

          {hasFilters && (
            <button onClick={clearFilters} style={S.clearFiltersBtn}>
              <Filter size={13} /> Clear
            </button>
          )}

          {/* Export */}
          <button onClick={handleExport} disabled={exporting} style={{ ...S.exportBtn, opacity: exporting ? 0.7 : 1 }}>
            <Download size={14} /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>

        {/* ── RESULTS META ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>
            {loading ? 'Loading…' : `${total} result${total !== 1 ? 's' : ''}${hasFilters ? ' (filtered)' : ''}`}
          </p>
          {pages > 1 && (
            <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>Page {page} of {pages}</p>
          )}
        </div>

        {/* ── TABLE ── */}
        <div style={S.tableWrap}>
          {loading ? (
            <div style={S.loading}>
              <RefreshCw size={20} color="#2563eb" style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ color: '#64748b', fontSize: '13px' }}>Loading leads…</span>
            </div>
          ) : leads.length === 0 ? (
            <div style={S.empty}>
              <Users size={32} color="#cbd5e1" />
              <p style={{ margin: '12px 0 4px', fontSize: '15px', fontWeight: 700, color: '#94a3b8' }}>No leads found</p>
              <p style={{ margin: 0, fontSize: '13px', color: '#cbd5e1' }}>{hasFilters ? 'Try adjusting your filters.' : 'Submissions will appear here.'}</p>
            </div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  {['Name & Company', 'Contact', 'Country', 'Industry', 'Type', 'Submitted', 'Message'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.map((lead, i) => {
                  const typeStyle = CONTACT_TYPE_COLORS[lead.contactType] || CONTACT_TYPE_COLORS.inquiry;
                  const isExp     = expanded === lead._id;
                  return (
                    <tr key={lead._id} style={{ background: i % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                      {/* Name + Company */}
                      <td style={S.td}>
                        <p style={{ margin: 0, fontWeight: 700, fontSize: '13px', color: '#0f172a' }}>{lead.name || '—'}</p>
                        {lead.company && <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#94a3b8' }}>{lead.company}</p>}
                      </td>

                      {/* Contact */}
                      <td style={S.td}>
                        <a href={`mailto:${lead.email}`} style={{ display: 'block', fontSize: '12px', color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}>{lead.email}</a>
                        {lead.phone && <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>{lead.phone}</p>}
                      </td>

                      {/* Country */}
                      <td style={S.td}>
                        <span style={{ fontSize: '12px', color: '#374151' }}>{COUNTRY_MAP[lead.country] || lead.country || '—'}</span>
                      </td>

                      {/* Industry */}
                      <td style={S.td}>
                        <span style={{ fontSize: '12px', color: '#374151' }}>{INDUSTRY_LABELS[lead.industry] || lead.industry || '—'}</span>
                      </td>

                      {/* Type badge */}
                      <td style={S.td}>
                        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '2px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em', background: typeStyle.bg, color: typeStyle.color, border: `1px solid ${typeStyle.border}` }}>
                          {CONTACT_TYPE_LABELS[lead.contactType] || lead.contactType || '—'}
                        </span>
                      </td>

                      {/* Date */}
                      <td style={{ ...S.td, whiteSpace: 'nowrap', fontSize: '12px', color: '#64748b' }}>
                        {fmt(lead.createdAt)}
                      </td>

                      {/* Message */}
                      <td style={{ ...S.td, maxWidth: '220px' }}>
                        {lead.message ? (
                          <>
                            <p style={{ margin: 0, fontSize: '12px', color: '#374151', lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: isExp ? 99 : 2, WebkitBoxOrient: 'vertical' }}>
                              {lead.message}
                            </p>
                            {lead.message.length > 80 && (
                              <button onClick={() => setExpanded(isExp ? null : lead._id)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '11px', color: '#2563eb', fontWeight: 600, marginTop: '4px' }}>
                                {isExp ? 'Less ↑' : 'More ↓'}
                              </button>
                            )}
                          </>
                        ) : <span style={{ color: '#cbd5e1', fontSize: '12px' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── PAGINATION ── */}
        {pages > 1 && !loading && (
          <div style={S.pagination}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ ...S.pageBtn, opacity: page === 1 ? 0.4 : 1 }}>
              <ChevronLeft size={15} /> Prev
            </button>
            <div style={{ display: 'flex', gap: '4px' }}>
              {Array.from({ length: Math.min(7, pages) }, (_, i) => {
                const n = pages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= pages - 3 ? pages - 6 + i : page - 3 + i;
                return (
                  <button key={n} onClick={() => setPage(n)} style={{ ...S.pageBtn, background: n === page ? '#0a0f1e' : '#f1f5f9', color: n === page ? '#ffffff' : '#374151', fontWeight: n === page ? 700 : 500 }}>
                    {n}
                  </button>
                );
              })}
            </div>
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} style={{ ...S.pageBtn, opacity: page === pages ? 0.4 : 1 }}>
              Next <ChevronRight size={15} />
            </button>
          </div>
        )}

      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
        select:focus, input:focus { outline: 2px solid #2563eb; outline-offset: -1px; }
        tr:hover td { background: #f0f7ff !important; }
      `}</style>
    </div>
  );
}

const styles = {
  page:    { minHeight: '100vh', background: '#f1f5f9', fontFamily: "-apple-system,'Segoe UI',Arial,sans-serif" },

  nav:     { background: '#080e1c', borderBottom: '1px solid rgba(255,255,255,0.07)', position: 'sticky', top: 0, zIndex: 100 },
  navInner:{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  navLeft: { display: 'flex', alignItems: 'center', gap: '14px' },
  navDivider: { width: '1px', height: '18px', background: 'rgba(255,255,255,0.12)' },
  navLabel: { color: 'rgba(255,255,255,0.4)', fontSize: '12px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' },
  navChip: { background: 'rgba(37,99,235,0.18)', border: '1px solid rgba(37,99,235,0.3)', color: '#93bbfd', fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '2px', letterSpacing: '0.06em' },
  iconBtn: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px', padding: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  logoutBtn: { display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, padding: '6px 14px', borderRadius: '4px', cursor: 'pointer' },

  content: { maxWidth: '1400px', margin: '0 auto', padding: '28px 24px 48px' },

  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px', marginBottom: '24px' },
  statCard: { background: '#ffffff', border: '1px solid', borderRadius: '4px', padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  statIcon: { width: '40px', height: '40px', borderRadius: '4px', border: '1px solid', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  statValue: { margin: 0, fontSize: '24px', fontWeight: 800, color: '#0a0f1e', letterSpacing: '-0.02em' },
  statLabel: { margin: '2px 0 0', fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' },

  filterBar: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '14px 16px', marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },

  searchWrap:  { position: 'relative', flex: '1', minWidth: '200px' },
  searchInput: { width: '100%', padding: '9px 36px 9px 36px', border: '1px solid #e2e8f0', borderRadius: '2px', fontSize: '13px', color: '#0f172a', background: '#f8fafc', fontFamily: 'inherit' },
  clearX:      { position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center', padding: 0 },

  select: { padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '2px', fontSize: '13px', color: '#374151', background: '#f8fafc', fontFamily: 'inherit', cursor: 'pointer' },

  clearFiltersBtn: { display: 'flex', alignItems: 'center', gap: '5px', padding: '9px 14px', background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', fontSize: '12px', fontWeight: 700, borderRadius: '2px', cursor: 'pointer' },
  exportBtn:       { display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 18px', background: '#0a0f1e', color: '#ffffff', border: 'none', borderRadius: '2px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', marginLeft: 'auto', letterSpacing: '0.03em' },

  tableWrap: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '4px', overflow: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: '16px' },
  table:     { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th:        { padding: '11px 16px', textAlign: 'left', fontSize: '10px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.12em', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
  td:        { padding: '12px 16px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' },

  loading:   { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '64px 0' },
  empty:     { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '64px 0', textAlign: 'center' },

  pagination: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' },
  pageBtn:    { display: 'flex', alignItems: 'center', gap: '4px', padding: '7px 14px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '2px', fontSize: '13px', fontWeight: 500, color: '#374151', cursor: 'pointer' },
};
